// Flight booking entities
import { getServiceFeeRate } from '../utils';
import { publicOrigin } from '@/lib/publicOrigin';

/** JSONB may arrive as a parsed object or a JSON string — normalize before reading slices, payment fields, etc. */
function normalizeFlightDataField(raw) {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return typeof p === 'object' && p !== null && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return {};
}

// Global auth error handler - detects session expiry and triggers logout
const handleAuthError = (response) => {
  if (response.status === 401 || response.status === 403) {
    console.warn('🚨 Auth error detected - session expired or invalid');
    // Dispatch custom event that Layout.jsx will listen to
    window.dispatchEvent(new CustomEvent('auth-session-expired', {
      detail: { status: response.status, timestamp: Date.now() }
    }));
  }
};

export class FlightBooking {
  constructor(data = {}) {
    this.id = data.id || null;
    this.userId = data.userId || data.user_id || null;
    this.offerId = data.offerId || data.offer_id || null;
    // CRITICAL: Prioritize flight_data.defera_booking_reference JSONB field (admin edits write here)
    // then fall back to booking_reference column for legacy data
    this.flightData = normalizeFlightDataField(data.flightData ?? data.flight_data);
    const flightDataRef = this.flightData?.defera_booking_reference;
    this.bookingReference = flightDataRef || data.bookingReference || data.booking_reference || null;
    this.tripReference = data.tripReference || data.trip_reference || null;
    this.confirmationNumber = data.confirmationNumber || data.confirmation_number || null;
    this.paymentUrl = data.paymentUrl || data.payment_url || null;
    // Store passengers from API join (for customer visibility of seat assignments)
    this.passengers = data.passengers || [];
    // Store stays from API join (for customer visibility of hotel data)
    this.stays = data.stays || null;
    // Faye travel insurance (migration 156): the premium inside this row's
    // totals, and the policy the booking endpoints attach like stays.
    this.insurance_premium_cents = data.insurance_premium_cents ?? data.insurancePremiumCents ?? null;
    this.trip_insurance = data.trip_insurance ?? data.tripInsurance ?? null;
    // Set by the paged admin list when the row's offer blob was stripped
    // (server/admin-bookings-paging.js). FlightDetailsModal hydrates such a
    // row through FlightBooking.fetchFull before it opens.
    this._slim = data._slim === true;
    this.flights = data.flights || (this.flightData && this.flightData.flights) || [];
    this.archived_flight_data = data.archived_flight_data || data.archivedFlightData || null;
    this.change_flight_history = data.change_flight_history || data.changeFlightHistory || [];
    this.totalPrice = data.totalPrice || data.total_price || data.total_amount || 0;
    // Mirror the snake_case DB column. Without this, FlightDetailsModal's "self-heal"
    // trigger (`storedGrandWithHold > storedTotalAmount + 1`) reads `undefined`, falls
    // through to 0, and incorrectly engages the flight_data-base path — re-rendering
    // the raw Duffel airline price ($1,739) instead of what the customer actually paid
    // ($1,538.84). PY2RE8 was the canary; every Capital booking with a price-down was
    // affected. Keep `totalPrice` in sync so legacy readers don't drift.
    this.total_amount = data.total_amount ?? data.totalAmount ?? this.totalPrice ?? 0;
    this.service_fee = data.service_fee || data.serviceFee || 0;
    this.grand_total = data.grand_total || data.grandTotal || 0;
    this.hold_fee = data.hold_fee || data.holdFee || 0;
    this.grand_total_with_hold = data.grand_total_with_hold || data.grandTotalWithHold || 0;
    this.currency = data.currency || data.total_currency || 'USD';
    this.passengerCount = data.passengerCount || data.passenger_count || 1;
    this.cabinClass = data.cabinClass || data.cabin_class || null;
    this.status = data.status || data.trip_status || this.flightData?.trip_status || 'pending';
    this.trip_status = data.trip_status || this.flightData?.trip_status || this.status;
    this.payment_method = data.payment_method || this.flightData?._payment_method || this.flightData?.payment_method || 'installments';
    // Custom title stored inside flight_data to avoid schema cache issues
    this.customTitle = this.flightData?.custom_title || data.customTitle || data.custom_title || null;
    // Miles earned stored inside flight_data to avoid schema cache issues
    this.milesEarned = parseFloat(this.flightData?.miles_earned || data.milesEarned || data.miles_earned || 0);
    // Backer ID for tracking which backer is assigned to this booking
    this.backer_id = data.backer_id || data.backerId || null;
    this.backerId = this.backer_id; // Alias for consistency
    // When backer_id was set to its current non-null value (migration 135).
    // Investor.jsx uses this to bucket a backer's earnings on the assignment
    // date instead of the booking date, so trips assigned long after booking
    // don't retroactively appear in Ravi's chart in the deep past. NULL when
    // no backer is assigned; falls back to `createdAt` at the caller.
    this.backer_assigned_at = data.backer_assigned_at || data.backerAssignedAt || null;
    this.backerAssignedAt = this.backer_assigned_at;
    this.createdAt = data.createdAt || data.created_at || new Date().toISOString();
    this.updatedAt = data.updatedAt || data.updated_at || new Date().toISOString();
    
    // Extract flight details from flight_data for Dashboard display
    const flightData = this.flightData || {};
    const slices = flightData.slices || [];
    const firstSlice = slices[0];
    const lastSlice = slices[slices.length - 1];
    
    // Extract departure and arrival information from the FIRST SLICE (outbound leg)
    // For display purposes, we show the outbound journey (e.g., SFO → DFW for a roundtrip SFO-DFW-SFO)
    if (firstSlice && firstSlice.segments && firstSlice.segments.length > 0) {
      const firstSegment = firstSlice.segments[0];
      this.departure_city = firstSegment?.origin?.iata_code || null;
      this.departure_time = firstSegment?.departing_at || null;
      // Extract just the date portion (YYYY-MM-DD) from departing_at
      const departingAt = firstSegment?.departing_at || null;
      if (departingAt) {
        // Handle both ISO format (2025-11-13T19:30) and space format (2025-11-13 19:30)
        this.departure_date = departingAt.includes('T') 
          ? departingAt.split('T')[0] 
          : departingAt.split(' ')[0];
      } else {
        this.departure_date = null;
      }
      this.marketing_airline = firstSegment?.marketing_carrier?.name || null;
      
      // For arrival, get the last segment's destination of the FIRST SLICE (outbound destination)
      const lastSegmentOfFirstSlice = firstSlice.segments[firstSlice.segments.length - 1];
      this.arrival_city = lastSegmentOfFirstSlice?.destination?.iata_code || null;
      this.arrival_time = lastSegmentOfFirstSlice?.arriving_at || null;
      this.arrival_date = lastSegmentOfFirstSlice?.arriving_at?.split('T')[0] || lastSegmentOfFirstSlice?.arriving_at?.split(' ')[0] || null;
    } else if (flightData.departure_city || flightData.arrival_city) {
      // Handle uploaded flight data (no slices structure)
      this.departure_city = flightData.departure_city || null;
      this.arrival_city = flightData.arrival_city || null;
      this.departure_date = flightData.departure_date || null;
      this.departure_time = flightData.departure_time || null;
      this.arrival_date = flightData.arrival_date || null;
      this.arrival_time = flightData.arrival_time || null;
      this.marketing_airline = flightData.marketing_airline || flightData.operating_airline || null;
    }
    
    // Extract price from flight_data if total_amount is 0
    if (this.totalPrice === 0 && flightData.total_amount) {
      this.totalPrice = parseFloat(flightData.total_amount) || 0;
      this.total_amount = this.totalPrice;
    }
    
    this.created_by = data.created_by || data.user_email || data.userId || data.user_id || null;
    this.created_date = this.createdAt;
    this.itinerary_image_url = flightData.itinerary_image_url || null;
    
    // Store stay_data as plain object (for Flights + Stays packages)
    // This can come from: 1) abandoned_trips.stay_data, 2) joined stays table data, or 3) stays array from query
    // Store as plain object to avoid temporal dead zone error (StayBooking class defined later in file)
    this.stay_data = data.stay_data || data.stayData || (data.stays && data.stays[0]) || null;
  }

  static async create(data) {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to create bookings');
    }

    const { supabase } = await import('../lib/supabase');
    const flightData = data.flightData || data.flight_data || {};
    if (data.flights && data.flights.length > 0) {
      flightData.flights = data.flights;
    }
    
    // Generate 6-character trip reference
    const generateTripReference = () => {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Exclude confusing chars like 0, O, 1, I
      let reference = '';
      for (let i = 0; i < 6; i++) {
        reference += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return reference;
    };
    
    const tripReference = data.tripReference || data.trip_reference || generateTripReference();
    
    // Calculate pricing if not provided
    const totalPrice = data.totalPrice || data.total_price || data.total_amount || 0;
    const pmCreate = flightData._payment_method || flightData.payment_method || 'installments';
    // Service fee (effective 2026-05-19): 40% (Slice) / 30% (Defera Capital) under $300; 8% for $300+ — keyed off payment method via getServiceFeeRate (single source of truth)
    const serviceFeeRate = getServiceFeeRate(totalPrice, pmCreate === 'defera_capital');
    const serviceFee = data.serviceFee || data.service_fee || (totalPrice * serviceFeeRate);
    const grandTotal = data.grandTotal || data.grand_total || (totalPrice + serviceFee);
    /** Hold fee is 15% for both Slice Pay and Defera Capital going forward. */
    const holdRateCreate = 0.15;
    const holdFee = data.holdFee || data.hold_fee || (grandTotal * holdRateCreate);
    const grandTotalWithHold = data.grandTotalWithHold || data.grand_total_with_hold || (grandTotal + holdFee);

    const bookingData = {
      user_id: data.user_id || data.userId || currentUser.id,
      offer_id: data.offerId || data.offer_id,
      booking_reference: data.bookingReference || data.booking_reference,
      trip_reference: tripReference,
      payment_url: data.paymentUrl || data.payment_url || null,
      status: data.status || 'pending',
      total_amount: totalPrice,
      service_fee: serviceFee,
      grand_total: grandTotal,
      hold_fee: holdFee,
      grand_total_with_hold: grandTotalWithHold,
      total_currency: data.currency || data.total_currency || 'USD',
      passenger_count: data.passengerCount || data.passenger_count || 1,
      cabin_class: data.cabinClass || data.cabin_class,
      flight_data: flightData
    };

    const { data: booking, error } = await supabase
      .from('bookings')
      .insert(bookingData)
      .select()
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return new FlightBooking(booking);
  }

  static async findById(id) {
    const { supabase } = await import('../lib/supabase');
    // LEFT JOIN with stays table using explicit relationship (stays.booking_id -> bookings.id)
    const { data, error } = await supabase
      .from('bookings')
      .select('*, stays!stays_booking_id_fkey(*)')
      .eq('id', id)
      .single();

    if (error) {
      return null;
    }

    return data ? new FlightBooking(data) : null;
  }

  static async findAll(filters = {}) {
    const { supabase } = await import('../lib/supabase');
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();

    if (!currentUser) {
      return {
        data: [],
        total: 0,
        page: 1,
        pages: 0,
        hasNext: false,
        hasPrev: false
      };
    }

    // LEFT JOIN with stays table using explicit relationship (stays.booking_id -> bookings.id)
    let query = supabase
      .from('bookings')
      .select('*, stays!stays_booking_id_fkey(*)', { count: 'exact' })
      .eq('user_id', currentUser.id);

    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    query = query.order('created_at', { ascending: false });

    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const startIndex = (page - 1) * limit;

    query = query.range(startIndex, startIndex + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      throw new Error(error.message);
    }

    const bookings = (data || []).map(b => new FlightBooking(b));
    const total = count || 0;
    const pages = Math.ceil(total / limit);

    return {
      data: bookings,
      total,
      page,
      pages,
      hasNext: page < pages,
      hasPrev: page > 1
    };
  }

  static async getById(id) {
    const { supabase } = await import('../lib/supabase');
    
    // Query bookings WITHOUT stays join to avoid permission issues
    const { data, error} = await supabase
      .from('bookings')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Failed to fetch booking:', error);
      return null;
    }

    return data ? new FlightBooking(data) : null;
  }

  static async filter(filters = {}, orderBy = '-created_at', limit = null) {
    const { supabase } = await import('../lib/supabase');
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();

    if (!currentUser) {
      return [];
    }

    // Query bookings WITHOUT stays join to avoid permission issues
    let query = supabase
      .from('bookings')
      .select('*');

    // Always filter by current user (RLS also enforces this)
    query = query.eq('user_id', currentUser.id);

    // Apply additional filters
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    // Apply ordering
    const isDescending = orderBy.startsWith('-');
    const column = isDescending ? orderBy.substring(1) : orderBy;
    query = query.order(column, { ascending: !isDescending });

    // Apply limit
    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Failed to filter bookings:', error);
      return [];
    }

    return (data || []).map(booking => new FlightBooking(booking));
  }

  static async list(orderBy = '-created_at', userId = null) {
    const { User } = await import('./entities');
    const { supabase } = await import('../lib/supabase');
    
    console.log('🔍 [FlightBooking.list] STEP 1: Checking session...');
    
    // STEP 1: Check session directly (source of truth)
    let session, sessionError;
    try {
      const result = await supabase.auth.getSession();
      session = result.data?.session;
      sessionError = result.error;
      console.log('🔍 [FlightBooking.list] Session check result:', { hasSession: !!session, error: sessionError?.message });
    } catch (err) {
      console.error('❌ [FlightBooking.list] STEP 1 FAILED: Session check threw exception:', err);
      throw new Error(`Session check failed: ${err.message}`);
    }
    
    // STEP 2: If no session, try refreshing before giving up
    if (!session || sessionError) {
      console.warn('⚠️ [FlightBooking.list] STEP 2: No active session found. Attempting session refresh...');
      
      try {
        const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !refreshedSession) {
          console.error('❌ [FlightBooking.list] STEP 2 FAILED: Session refresh failed:', refreshError?.message);
          throw new Error('Your session has expired. Please log in again to view your trips.');
        }
        
        session = refreshedSession;
        console.log('✅ [FlightBooking.list] STEP 2 SUCCESS: Session refreshed successfully');
      } catch (err) {
        console.error('❌ [FlightBooking.list] STEP 2 FAILED: Session refresh threw exception:', err);
        throw err;
      }
    }
    
    console.log('🔍 [FlightBooking.list] STEP 3: Getting current user...');
    
    // STEP 3: Session exists - now try to get user object
    let currentUser;
    try {
      currentUser = await User.getCurrentUser();
      console.log('🔍 [FlightBooking.list] User.getCurrentUser() result:', { hasUser: !!currentUser, email: currentUser?.email });
    } catch (err) {
      console.error('❌ [FlightBooking.list] STEP 3 FAILED: User.getCurrentUser() threw exception:', err);
      throw new Error(`User retrieval failed: ${err.message}`);
    }
    
    // STEP 4: If getCurrentUser fails despite valid session, hydrate from session
    if (!currentUser && session) {
      console.warn('⚠️ [FlightBooking.list] STEP 4: Valid session exists but User.getCurrentUser() failed. Attempting user hydration...');
      
      try {
        // Clear potentially corrupted localStorage cache
        localStorage.removeItem('defera.user');
        console.log('🔍 [FlightBooking.list] Cleared corrupted localStorage cache');
        
        // Fetch fresh user data from Supabase
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError || !user) {
          console.error('❌ [FlightBooking.list] STEP 4 FAILED: User hydration failed:', userError?.message);
          throw new Error('Unable to load your profile. Please refresh the page.');
        }
        
        console.log('✅ [FlightBooking.list] User data fetched from Supabase, constructing User object');
        
        // Determine role from app_metadata (security: don't trust user_metadata)
        const roles = user.app_metadata?.roles || [];
        const isAdmin = roles.includes('admin');
        const isInvestor = roles.includes('investor');
        
        let userRole = 'user';
        if (isAdmin) {
          userRole = 'admin';
        } else if (isInvestor) {
          userRole = 'investor';
        } else if (roles.includes('concierge')) {
          // Never collapses to 'admin': a stale cached `role` field is read by
          // UI that gates edit controls, and concierge must not inherit those.
          userRole = 'concierge';
        }
        
        console.log('🔍 [FlightBooking.list] Determined role:', userRole, '| Roles array:', roles);
        
        // Construct User object directly from fetched data
        currentUser = new User({
          id: user.id,
          email: user.email,
          firstName: user.user_metadata?.first_name || user.user_metadata?.firstName || '',
          lastName: user.user_metadata?.last_name || user.user_metadata?.lastName || '',
          role: userRole,
          app_metadata: user.app_metadata || {},
          isAuthenticated: true,
          createdAt: user.created_at
        });
        
        // Update singleton immediately for same-tick access
        User.setCurrentUser(currentUser);
        console.log('🔍 [FlightBooking.list] User singleton updated');
        
        // Cache the hydrated user for future calls (across tabs/sessions)
        const userDataToStore = {
          id: user.id,
          email: user.email,
          firstName: user.user_metadata?.first_name || user.user_metadata?.firstName || '',
          lastName: user.user_metadata?.last_name || user.user_metadata?.lastName || '',
          role: userRole,
          profile_photo: user.user_metadata?.profile_photo || null,
          createdAt: user.created_at,
          version: 4  // Version 4: Supports investor role
        };
        
        localStorage.setItem('defera.user', JSON.stringify(userDataToStore));
        console.log('✅ [FlightBooking.list] STEP 4 SUCCESS: User object created, singleton updated, and cached successfully from hydration');
        
      } catch (hydrationErr) {
        console.error('❌ [FlightBooking.list] STEP 4 FAILED: Hydration threw exception:', hydrationErr);
        throw hydrationErr;
      }
    }

    // Final safety check: Ensure we have a valid user object before proceeding
    if (!currentUser) {
      console.error('❌ [FlightBooking.list] FINAL CHECK FAILED: No current user after all auth attempts. Session exists:', !!session);
      throw new Error('Unable to authenticate. Please log out and log in again.');
    }

    console.log('✅ [FlightBooking.list] STEP 5: User authenticated successfully:', currentUser.email, '| Role:', currentUser.role);

    // Check if user is admin, investor, or backer
    // SECURITY: ONLY trust app_metadata.roles, never trust user.role field (can be corrupted cache)
    const roles = currentUser.app_metadata?.roles || [];
    const isAdmin = roles.includes('admin');
    const isInvestor = roles.includes('investor');
    const isBacker = roles.includes('backer');
    // Desk-only role: reads the whole book so the inquiry queue can see
    // passenger details. Read only — it cannot reach update-booking, which
    // still checks isUserAdmin server-side. See src/utils/roles.js.
    const isConcierge = roles.includes('concierge');
    
    console.log('🔍 [FlightBooking.list] Role determination:', {
      rolesArray: roles,
      isAdmin,
      isInvestor,
      isBacker,
      userRoleField: currentUser.role,
      note: 'Only app_metadata.roles is trusted for security'
    });

    // Admin/Investor/Backer users: Use backend endpoint to bypass RLS and see bookings
    // Backers need this endpoint to see bookings assigned to them (by backer_id)
    if (isAdmin || isInvestor || isBacker || isConcierge) {
      console.log('🔍 [FlightBooking.list] STEP 6: Admin/Investor path - fetching all bookings from backend API');
      
      try {
        // supabase already imported at top of method
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.access_token) {
          console.error('❌ [FlightBooking.list] STEP 6 FAILED: No valid session for admin/investor bookings fetch');
          return [];
        }

        console.log('🔍 [FlightBooking.list] Calling /api/admin/all-bookings...');
        console.log('🔍 [FlightBooking.list] Request details:', {
          url: '/api/admin/all-bookings',
          method: 'POST',
          hasToken: !!session.access_token,
          tokenLength: session.access_token?.length
        });
        
        // Paged (2026-09-16): one unpaginated reply had grown to 9 MB, past
        // Vercel's 4.5 MB serverless response cap, and this function's
        // `return []` on failure is exactly why the Trips page showed
        // "Your Portfolio is Empty" instead of an error. Pages of 100
        // concatenate into the same list; see src/api/adminBookingsPager.js.
        const fetchStartTime = Date.now();
        let responseData;
        try {
          const { fetchAllAdminBookings } = await import('./adminBookingsPager.js');
          responseData = await fetchAllAdminBookings(session.access_token);
          console.log('✅ [FlightBooking.list] Fetched', responseData.bookings.length, 'bookings in', responseData.pages, 'page(s),', Date.now() - fetchStartTime, 'ms');
        } catch (fetchError) {
          console.error('❌ [FlightBooking.list] STEP 6 FAILED: paged fetch failed:', fetchError?.message || fetchError);
          return [];
        }

        const { bookings } = responseData || {};
        console.log('✅ [FlightBooking.list] STEP 6 SUCCESS: Fetched', bookings?.length || 0, 'bookings from admin API');
        // Log first booking to verify passengers, stays, and backer_id are included
        if (bookings && bookings.length > 0) {
          console.log('🔍 [FlightBooking.list] Admin/Investor/Backer - First booking data check:', {
            id: bookings[0].id,
            hasPassengers: !!bookings[0].passengers,
            passengersCount: bookings[0].passengers?.length || 0,
            hasStays: !!bookings[0].stays,
            hasFlightData: !!bookings[0].flight_data,
            backer_id: bookings[0].backer_id,
            hasBackerId: 'backer_id' in bookings[0]
          });
          
          // Log bookings with backer_id for debugging
          const bookingsWithBacker = bookings.filter(b => b.backer_id);
          console.log('🔍 [FlightBooking.list] Bookings with backer_id:', bookingsWithBacker.length, 'out of', bookings.length);
          if (bookingsWithBacker.length > 0) {
            console.log('🔍 [FlightBooking.list] Sample backer_id values:', bookingsWithBacker.slice(0, 3).map(b => ({
              id: b.id,
              backer_id: b.backer_id
            })));
          }
        }
        return (bookings || []).map(booking => new FlightBooking(booking));
      } catch (error) {
        console.error('❌ [FlightBooking.list] STEP 6 FAILED: Exception in admin/investor bookings fetch:', error);
        console.error('❌ [FlightBooking.list] Error stack:', error.stack);
        return [];
      }
    }

    // Regular users: Use backend API endpoint to fetch bookings with stays (bypasses RLS permission issues)
    console.log('🔍 [FlightBooking.list] STEP 6: Regular user path - fetching user bookings via backend API');
    
    try {
      // Get session for auth token
      const { data: { session: userSession } } = await supabase.auth.getSession();
      
      if (!userSession?.access_token) {
        console.error('❌ [FlightBooking.list] No valid session for user bookings fetch');
        return [];
      }

      console.log('🔍 [FlightBooking.list] Calling /api/user/bookings...');
      
      const response = await fetch('/api/user/bookings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userSession.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('❌ [FlightBooking.list] STEP 6 FAILED: User bookings API returned error:', response.status);
        const errorText = await response.text();
        console.error('❌ [FlightBooking.list] Error details:', errorText);
        return [];
      }

      const { bookings } = await response.json();
      console.log('✅ [FlightBooking.list] STEP 6 SUCCESS: Fetched', bookings?.length || 0, 'bookings from user API');
      // Log first booking to verify passengers and stays are included
      if (bookings && bookings.length > 0) {
        console.log('🔍 [FlightBooking.list] First booking data check:', {
          id: bookings[0].id,
          hasPassengers: !!bookings[0].passengers,
          passengersCount: bookings[0].passengers?.length || 0,
          hasStays: !!bookings[0].stays,
          hasFlightData: !!bookings[0].flight_data
        });
      }
      return (bookings || []).map(booking => new FlightBooking(booking));
    } catch (err) {
      console.error('❌ [FlightBooking.list] STEP 6 FAILED: Exception in regular user bookings fetch:', err);
      console.error('❌ [FlightBooking.list] Error stack:', err.stack);
      throw err;
    }
  }

  // Always returns the caller's OWN customer bookings (user_id = caller),
  // regardless of role. Used by the Trips/Dashboard page so that backers
  // and investors never receive backer-assigned or all-bookings data over
  // the wire — only the trips they themselves booked as a customer.
  /**
   * Full rows for the given booking ids through POST /api/admin/booking.
   * The paged admin list ships rows slim (offer blob stripped, `_slim: true`);
   * anything that renders or edits a booking in depth hydrates it here first
   * (src/components/flight/useHydratedTrip.js). Throws on failure so the
   * caller decides how to degrade; never returns a partial list as complete.
   */
  static async fetchFull(ids, { references = null } = {}) {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Authentication required');
    const response = await fetch('/api/admin/booking', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(references ? { references } : { ids })
    });
    if (!response.ok) throw new Error(`Booking hydration failed: HTTP ${response.status}`);
    const { bookings } = await response.json();
    return (bookings || []).map((b) => new FlightBooking(b));
  }

  static async listMyOwn() {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      console.error('❌ [FlightBooking.listMyOwn] No session');
      return [];
    }

    try {
      const response = await fetch('/api/user/bookings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('❌ [FlightBooking.listMyOwn] API error:', response.status);
        return [];
      }

      const { bookings } = await response.json();
      return (bookings || []).map(booking => new FlightBooking(booking));
    } catch (err) {
      console.error('❌ [FlightBooking.listMyOwn] Exception:', err);
      return [];
    }
  }

  static async update(id, updates) {
    const { supabase } = await import('../lib/supabase');
    
    const updateData = {};
    if (updates.payment_link !== undefined) updateData.payment_link = updates.payment_link;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.booking_reference !== undefined) updateData.booking_reference = updates.booking_reference;
    if (updates.trip_reference !== undefined) updateData.trip_reference = updates.trip_reference;
    if (updates.confirmation_number !== undefined) updateData.confirmation_number = updates.confirmation_number;
    if (updates.confirmationNumber !== undefined) updateData.confirmation_number = updates.confirmationNumber;
    if (updates.payment_url !== undefined) updateData.payment_url = updates.payment_url;
    if (updates.offer_id !== undefined) updateData.offer_id = updates.offer_id;
    if (updates.total_amount !== undefined) updateData.total_amount = updates.total_amount;
    if (updates.service_fee !== undefined) updateData.service_fee = updates.service_fee;
    if (updates.grand_total !== undefined) updateData.grand_total = updates.grand_total;
    if (updates.hold_fee !== undefined) updateData.hold_fee = updates.hold_fee;
    if (updates.grand_total_with_hold !== undefined) updateData.grand_total_with_hold = updates.grand_total_with_hold;
    if (updates.total_currency !== undefined) updateData.total_currency = updates.total_currency;
    if (updates.passenger_count !== undefined) updateData.passenger_count = updates.passenger_count;
    if (updates.cabin_class !== undefined) updateData.cabin_class = updates.cabin_class;
    if (updates.flight_data !== undefined) updateData.flight_data = updates.flight_data;
    if (updates.flight_changes !== undefined) updateData.flight_changes = updates.flight_changes;

    const { data, error } = await supabase
      .from('bookings')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return new FlightBooking(data);
  }

  async save() {
    const { supabase } = await import('../lib/supabase');
    
    const flightData = { ...this.flightData };
    if (this.flights && this.flights.length > 0) {
      flightData.flights = this.flights;
    }
    
    const bookingData = {
      offer_id: this.offerId,
      booking_reference: this.bookingReference,
      trip_reference: this.tripReference,
      payment_url: this.paymentUrl,
      status: this.status,
      total_amount: this.totalPrice,
      total_price: this.total_price || this.totalPrice,
      service_fee: this.service_fee || 0,
      grand_total: this.grand_total || 0,
      hold_fee: this.hold_fee || 0,
      grand_total_with_hold: this.grand_total_with_hold || 0,
      total_currency: this.currency,
      passenger_count: this.passengerCount,
      cabin_class: this.cabinClass,
      flight_data: flightData
    };

    if (this.id) {
      const { data, error } = await supabase
        .from('bookings')
        .update(bookingData)
        .eq('id', this.id)
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      Object.assign(this, new FlightBooking(data));
    } else {
      const { User } = await import('./entities');
      const currentUser = await User.getCurrentUser();

      if (!currentUser) {
        throw new Error('User must be logged in');
      }

      const { data, error } = await supabase
        .from('bookings')
        .insert({ ...bookingData, user_id: currentUser.id })
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      Object.assign(this, new FlightBooking(data));
    }

    return this;
  }

  async delete() {
    if (!this.id) {
      throw new Error('Cannot delete booking without ID');
    }

    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase
      .from('bookings')
      .delete()
      .eq('id', this.id);

    if (error) {
      throw new Error(error.message);
    }

    return true;
  }

  // Static method to delete a booking by ID
  // For admin users, use admin API endpoint to bypass RLS
  static async delete(id) {
    if (!id) {
      throw new Error('Cannot delete booking without ID');
    }

    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Authentication required to delete booking');
    }

    // Check if user is admin - if so, use admin endpoint to bypass RLS
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();
    
    if (currentUser?.role === 'admin') {
      // Use admin API endpoint to bypass RLS
      try {
        const response = await fetch(`/api/admin/bookings/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || `Failed to delete booking: ${response.status}`);
        }

        return true;
      } catch (fetchError) {
        // Fallback to direct Supabase delete if admin endpoint doesn't exist
        console.warn('Admin endpoint failed, trying direct delete:', fetchError.message);
      }
    }

    // Fallback: Direct Supabase delete (works if user owns the booking)
    const { error } = await supabase
      .from('bookings')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(error.message);
    }

    return true;
  }
}

export class Passenger {
  constructor(data = {}) {
    this.id = data.id || null;
    this.userId = data.userId || data.user_id || null;
    this.bookingId = data.bookingId || data.booking_id || null;
    this.type = data.type || 'adult';
    this.title = data.title || '';
    this.givenName = data.givenName || data.given_name || data.first_name || data.firstName || '';
    this.familyName = data.familyName || data.family_name || data.last_name || data.lastName || '';
    this.firstName = data.firstName || data.first_name || data.given_name || data.givenName || '';
    this.middleName = data.middleName || data.middle_name || '';
    this.lastName = data.lastName || data.last_name || data.family_name || data.familyName || '';
    this.gender = data.gender || '';
    this.bornOn = data.bornOn || data.born_on || data.date_of_birth || data.dateOfBirth || '';
    this.email = data.email || '';
    this.phoneNumber = data.phoneNumber || data.phone_number || data.phone || '';
    this.passportNumber = data.passportNumber || data.passport_number || data.passport || '';
    this.passportExpiryDate = data.passportExpiryDate || data.passport_expiry_date || data.passport_expiry || '';
    this.passportCountry = data.passportCountry || data.passport_country || '';
    this.nationality = data.nationality || '';
    this.frequentFlyerProgram = data.frequentFlyerProgram || data.frequent_flyer_program || '';
    this.frequentFlyerNumber = data.frequentFlyerNumber || data.frequent_flyer_number || '';
    this.knownTravelerNumber = data.knownTravelerNumber || data.known_traveler_number || '';
    this.specialRequests = data.specialRequests || data.special_requests || '';
    this.seatPreference = data.seatPreference || data.seat_preference || '';
    // Wheelchair assistance answers (migration 163), or null.
    this.wheelchairAssistance = data.wheelchairAssistance || data.wheelchair_assistance || null;
    this.createdAt = data.createdAt || data.created_at || new Date().toISOString();
    this.updatedAt = data.updatedAt || data.updated_at || new Date().toISOString();
  }

  static async create(data) {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to create passengers');
    }

    const { supabase } = await import('../lib/supabase');
    const passengerData = {
      user_id: currentUser.id,
      booking_id: data.bookingId || data.booking_id || null,
      type: data.type || 'adult',
      title: data.title || '',
      given_name: data.givenName || data.given_name || data.first_name || data.firstName || '',
      middle_name: data.middleName || data.middle_name || '',
      family_name: data.familyName || data.family_name || data.last_name || data.lastName || '',
      gender: data.gender || '',
      email: data.email || '',
      phone_number: data.phoneNumber || data.phone_number || data.phone || '',
      born_on: data.bornOn || data.born_on || data.date_of_birth || data.dateOfBirth || null,
      passport_number: data.passportNumber || data.passport_number || data.passport || '',
      passport_expiry_date: data.passportExpiryDate || data.passport_expiry_date || data.passport_expiry || null,
      passport_country: data.passportCountry || data.passport_country || '',
      nationality: data.nationality || '',
      frequent_flyer_program: data.frequentFlyerProgram || data.frequent_flyer_program || '',
      frequent_flyer_number: data.frequentFlyerNumber || data.frequent_flyer_number || '',
      known_traveler_number: data.knownTravelerNumber || data.known_traveler_number || '',
      special_requests: data.specialRequests || data.special_requests || '',
      seat_preference: data.seatPreference || data.seat_preference || ''
    };

    const { data: passenger, error } = await supabase
      .from('passengers')
      .insert(passengerData)
      .select()
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return new Passenger(passenger);
  }

  static async findById(id) {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('passengers')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      return null;
    }

    return data ? new Passenger(data) : null;
  }

  static async findByUserId(userId) {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('passengers')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(error.message);
    }

    return (data || []).map(p => new Passenger(p));
  }

  static async findByBookingId(bookingId) {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('passengers')
      .select('*')
      .eq('booking_id', bookingId);

    if (error) {
      throw new Error(error.message);
    }

    return (data || []).map(p => new Passenger(p));
  }

  async save() {
    const { supabase } = await import('../lib/supabase');
    
    const passengerData = {
      booking_id: this.bookingId,
      type: this.type || 'adult',
      title: this.title || '',
      given_name: this.givenName || this.firstName,
      middle_name: this.middleName || '',
      family_name: this.familyName || this.lastName,
      gender: this.gender || '',
      email: this.email || '',
      phone_number: this.phoneNumber || '',
      born_on: this.bornOn || null,
      passport_number: this.passportNumber || '',
      passport_expiry_date: this.passportExpiryDate || null,
      passport_country: this.passportCountry || '',
      nationality: this.nationality || '',
      frequent_flyer_program: this.frequentFlyerProgram || '',
      frequent_flyer_number: this.frequentFlyerNumber || '',
      known_traveler_number: this.knownTravelerNumber || '',
      special_requests: this.specialRequests || '',
      seat_preference: this.seatPreference || ''
    };

    console.log('💾 Passenger.save() called:', {
      id: this.id,
      given_name: passengerData.given_name,
      family_name: passengerData.family_name,
      booking_id: passengerData.booking_id
    });

    if (this.id) {
      const { data, error } = await supabase
        .from('passengers')
        .update(passengerData)
        .eq('id', this.id)
        .select()
        .single();

      if (error) {
        console.error('❌ Passenger update failed:', error);
        throw new Error(error.message);
      }

      console.log('✅ Passenger updated in DB:', {
        id: data.id,
        given_name: data.given_name,
        family_name: data.family_name
      });

      Object.assign(this, new Passenger(data));
    } else {
      const { User } = await import('./entities');
      const currentUser = await User.getCurrentUser();

      if (!currentUser) {
        throw new Error('User must be logged in');
      }

      const { data, error } = await supabase
        .from('passengers')
        .insert({ ...passengerData, user_id: currentUser.id })
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      Object.assign(this, new Passenger(data));
    }

    return this;
  }

  static async create(data) {
    const passenger = new Passenger(data);
    return await passenger.save();
  }

  async delete() {
    if (!this.id) {
      throw new Error('Cannot delete passenger without ID');
    }

    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase
      .from('passengers')
      .delete()
      .eq('id', this.id);

    if (error) {
      throw new Error(error.message);
    }

    return true;
  }
}

export class SavedPassenger {
  static async findByUserId(userId) {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('passengers')
      .select('*')
      .eq('user_id', userId)
      .is('booking_id', null)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(error.message);
    }

    return (data || []).map(p => new Passenger(p));
  }

  static async filter(criteria) {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to view saved passengers');
    }

    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('passengers')
      .select('*')
      .eq('user_id', currentUser.id)
      .is('booking_id', null)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(error.message);
    }

    return (data || []).map(p => ({
      id: p.id,
      first_name: p.given_name || '',
      middle_name: p.middle_name || '',
      last_name: p.family_name || '',
      gender: p.gender || '',
      date_of_birth: p.born_on || '',
      email: p.email || '',
      phone: p.phone_number || '',
      passport_number: p.passport_number || '',
      passport_expiry: p.passport_expiry_date || '',
      nationality: p.nationality || '',
      passport_country: p.passport_country || '',
      frequent_flyer_program: p.frequent_flyer_program || '',
      frequent_flyer_number: p.frequent_flyer_number || '',
      known_traveler_number: p.known_traveler_number || '',
      seat_preference: p.seat_preference || '',
      created_at: p.created_at,
      updated_at: p.updated_at
    }));
  }

  static async create(data) {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to create saved passengers');
    }

    const { supabase } = await import('../lib/supabase');
    
    // Build passenger data - map to actual Supabase column names
    const passengerData = {
      user_id: currentUser.id,
      booking_id: null,
      type: 'adult',
      given_name: data.first_name || '',
      family_name: data.last_name || ''
    };
    
    // Add optional fields only if they have values
    if (data.middle_name) passengerData.middle_name = data.middle_name;
    if (data.gender) passengerData.gender = data.gender;
    if (data.email) passengerData.email = data.email;
    if (data.phone) passengerData.phone_number = data.phone;
    if (data.date_of_birth) passengerData.born_on = data.date_of_birth;
    if (data.passport_number) passengerData.passport_number = data.passport_number;
    if (data.passport_expiry) passengerData.passport_expiry_date = data.passport_expiry;
    if (data.nationality) passengerData.nationality = data.nationality;
    if (data.passport_country) passengerData.passport_country = data.passport_country;
    if (data.frequent_flyer_program) passengerData.frequent_flyer_program = data.frequent_flyer_program;
    if (data.frequent_flyer_number) passengerData.frequent_flyer_number = data.frequent_flyer_number;
    if (data.known_traveler_number) passengerData.known_traveler_number = data.known_traveler_number;
    if (data.seat_preference) passengerData.seat_preference = data.seat_preference;

    console.log('Inserting saved traveler');

    const { data: newPassenger, error } = await supabase
      .from('passengers')
      .insert(passengerData)
      .select()
      .single();

    if (error) {
      console.error('Error creating saved passenger:', error);
      throw new Error(error.message);
    }

    return {
      id: newPassenger.id,
      first_name: newPassenger.given_name || '',
      middle_name: newPassenger.middle_name || '',
      last_name: newPassenger.family_name || '',
      gender: newPassenger.gender || '',
      date_of_birth: newPassenger.born_on || '',
      email: newPassenger.email || '',
      phone: newPassenger.phone_number || '',
      passport_number: newPassenger.passport_number || '',
      passport_expiry: newPassenger.passport_expiry_date || '',
      nationality: newPassenger.nationality || '',
      passport_country: newPassenger.passport_country || '',
      frequent_flyer_program: newPassenger.frequent_flyer_program || '',
      frequent_flyer_number: newPassenger.frequent_flyer_number || '',
      known_traveler_number: newPassenger.known_traveler_number || '',
      seat_preference: newPassenger.seat_preference || '',
      created_at: newPassenger.created_at,
      updated_at: newPassenger.updated_at
    };
  }

  static async update(id, data) {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to update saved passengers');
    }

    const { supabase } = await import('../lib/supabase');
    
    // Build update data - map to actual Supabase column names
    const updateData = {
      given_name: data.first_name || '',
      family_name: data.last_name || ''
    };
    
    // Add optional fields only if they have values.
    // middle_name intentionally always written (not gated on truthiness) so
    // clearing a middle name in the form actually clears it in the profile.
    updateData.middle_name = data.middle_name || '';
    if (data.gender) updateData.gender = data.gender;
    if (data.email) updateData.email = data.email;
    if (data.phone) updateData.phone_number = data.phone;
    if (data.date_of_birth) updateData.born_on = data.date_of_birth;
    if (data.passport_number) updateData.passport_number = data.passport_number;
    if (data.passport_expiry) updateData.passport_expiry_date = data.passport_expiry;
    if (data.nationality) updateData.nationality = data.nationality;
    if (data.passport_country) updateData.passport_country = data.passport_country;
    if (data.frequent_flyer_program) updateData.frequent_flyer_program = data.frequent_flyer_program;
    if (data.frequent_flyer_number) updateData.frequent_flyer_number = data.frequent_flyer_number;
    if (data.known_traveler_number) updateData.known_traveler_number = data.known_traveler_number;
    if (data.seat_preference) updateData.seat_preference = data.seat_preference;

    console.log('Updating saved traveler', id);

    const { data: updatedPassenger, error } = await supabase
      .from('passengers')
      .update(updateData)
      .eq('id', id)
      .eq('user_id', currentUser.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating saved passenger:', error);
      throw new Error(error.message);
    }

    return {
      id: updatedPassenger.id,
      first_name: updatedPassenger.given_name || '',
      middle_name: updatedPassenger.middle_name || '',
      last_name: updatedPassenger.family_name || '',
      gender: updatedPassenger.gender || '',
      date_of_birth: updatedPassenger.born_on || '',
      email: updatedPassenger.email || '',
      phone: updatedPassenger.phone_number || '',
      passport_number: updatedPassenger.passport_number || '',
      passport_expiry: updatedPassenger.passport_expiry_date || '',
      nationality: updatedPassenger.nationality || '',
      passport_country: updatedPassenger.passport_country || '',
      frequent_flyer_program: updatedPassenger.frequent_flyer_program || '',
      frequent_flyer_number: updatedPassenger.frequent_flyer_number || '',
      known_traveler_number: updatedPassenger.known_traveler_number || '',
      seat_preference: updatedPassenger.seat_preference || '',
      created_at: updatedPassenger.created_at,
      updated_at: updatedPassenger.updated_at
    };
  }

  static async delete(id) {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to delete saved passengers');
    }

    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('passengers')
      .delete()
      .eq('id', id)
      .eq('user_id', currentUser.id)
      .is('booking_id', null)
      .select('id');

    if (error) {
      console.error('Error deleting saved passenger:', error);
      throw new Error(error.message);
    }
    // A delete the database refuses matches no rows rather than erroring;
    // without this the traveler vanished from the list and came back on reload.
    if (!data?.length) {
      throw new Error('Saved traveler was not deleted');
    }

    return true;
  }

  static async upsert(data) {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to save passengers');
    }

    const { supabase } = await import('../lib/supabase');
    
    // Normalize data for consistent matching
    const normalizedFirstName = (data.first_name || '').trim().toLowerCase();
    const normalizedLastName = (data.last_name || '').trim().toLowerCase();
    const normalizedDOB = data.date_of_birth || '';
    
    if (!normalizedFirstName || !normalizedLastName || !normalizedDOB) {
      throw new Error('First name, last name, and date of birth are required');
    }
    
    // Check if passenger already exists based on normalized name and DOB
    const { data: existing, error: findError } = await supabase
      .from('passengers')
      .select('id')
      .eq('user_id', currentUser.id)
      .is('booking_id', null)
      .ilike('given_name', normalizedFirstName)
      .ilike('family_name', normalizedLastName)
      .eq('born_on', normalizedDOB)
      .maybeSingle();

    if (findError && findError.code !== 'PGRST116') {
      console.error('Error checking for existing passenger:', findError);
      throw new Error(findError.message);
    }

    // If exists, update; otherwise, create
    if (existing) {
      return await SavedPassenger.update(existing.id, data);
    } else {
      return await SavedPassenger.create(data);
    }
  }
}

/**
 * Account-owner frequent flyer wallet (user_loyalty_accounts, migration 093).
 * One row per airline program; auto-applied at checkout when the itinerary's
 * marketing carrier matches (see PassengerForm.jsx).
 */
export class UserLoyaltyAccount {
  static async list() {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();

    if (!currentUser) {
      throw new Error('User must be logged in to view loyalty accounts');
    }

    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('user_loyalty_accounts')
      .select('*')
      .eq('user_id', currentUser.id)
      .order('created_at', { ascending: true });

    if (error) {
      throw new Error(error.message);
    }

    return data || [];
  }

  static async upsert({ program_key, airline_iata, account_number }) {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();

    if (!currentUser) {
      throw new Error('User must be logged in to save loyalty accounts');
    }

    if (!program_key || !airline_iata || !account_number) {
      throw new Error('Program, airline, and account number are required');
    }

    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('user_loyalty_accounts')
      .upsert(
        {
          user_id: currentUser.id,
          program_key,
          airline_iata: airline_iata.toUpperCase(),
          account_number: account_number.trim(),
        },
        { onConflict: 'user_id,program_key' }
      )
      .select()
      .single();

    if (error) {
      console.error('Error saving loyalty account:', error);
      throw new Error(error.message);
    }

    return data;
  }

  static async delete(id) {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();

    if (!currentUser) {
      throw new Error('User must be logged in to delete loyalty accounts');
    }

    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase
      .from('user_loyalty_accounts')
      .delete()
      .eq('id', id)
      .eq('user_id', currentUser.id);

    if (error) {
      console.error('Error deleting loyalty account:', error);
      throw new Error(error.message);
    }

    return true;
  }
}

export class AbandonedTrip {
  constructor(data = {}) {
    this.id = data.id || null;
    this.user_id = data.user_id || data.userId || null;
    this.session_id = data.session_id || data.sessionId || null;
    this.user_email = data.user_email || data.userEmail || null;
    this.booking_reference = data.booking_reference || data.bookingReference || null;
    this.flight_data = data.flight_data || data.flightData || null;
    this.seat_selections = data.seat_selections || data.seatSelections || null;
    this.passengers = data.passengers || null;
    
    // Parse stay_data into StayBooking entity if it exists
    const stayData = data.stay_data || data.stayData || null;
    this.stay_data = stayData ? new StayBooking(stayData) : null;
    
    this.total_amount = data.total_amount || data.totalAmount || 0;
    this.departure_city = data.departure_city || data.departureCity || null;
    this.arrival_city = data.arrival_city || data.arrivalCity || null;
    this.departure_date = data.departure_date || data.departureDate || null;
    this.trip_type = data.trip_type || data.tripType || null;
    this.marketing_airline = data.marketing_airline || data.marketingAirline || null;
    this.status = data.status || 'in_cart';
    this.step_reached = data.step_reached || data.stepReached || null;
    this.last_activity = data.last_activity || data.lastActivity || new Date().toISOString();
    this.created_at = data.created_at || data.createdAt || new Date().toISOString();
    this.updated_at = data.updated_at || data.updatedAt || new Date().toISOString();
  }

  static async create(data) {
    // Signed in: the customer's own row through PostgREST. Guest: /api/guest-cart
    // (migration 160 keeps the anon key off abandoned_trips).
    const { getCartStore } = await import('./cartStore');
    const store = await getCartStore();
    const userId = store.userId;

    // CRITICAL: Require session_id to prevent orphan carts
    if (!data.session_id) {
      console.error('❌ AbandonedTrip.create called without session_id - aborting to prevent duplicate');
      throw new Error('session_id is required for cart creation');
    }

    // Insert only columns recognized by PostgREST schema cache
    // booking_reference, payment_method, miles_to_deplete stored in flight_data JSONB to bypass schema cache issue
    const flightDataWithPaymentInfo = {
      ...(data.flight_data || {}),
      _payment_method: data.payment_method || null,
      _miles_to_deplete: data.miles_to_deplete || 0
    };
    
    const tripData = {
      user_id: userId,
      session_id: data.session_id,
      user_email: data.user_email,
      flight_data: flightDataWithPaymentInfo,
      seat_selections: data.seat_selections,
      passengers: data.passengers || null,
      total_amount: data.total_amount,
      departure_city: data.departure_city,
      arrival_city: data.arrival_city,
      departure_date: data.departure_date,
      trip_type: data.trip_type,
      marketing_airline: data.marketing_airline,
      status: data.status || 'in_cart',
      step_reached: data.step_reached,
      last_activity: data.last_activity || new Date().toISOString()
    };

    // STRATEGY: First check if cart exists, then update or insert
    // Avoids relying on Supabase upsert which has schema cache issues
    
    // Step 1: Check if cart already exists for this session
    // (the most recent one: legacy duplicate session_ids exist)
    const { data: existingCart, error: findError } = await store.latestBySession(data.session_id);

    if (findError) {
      console.error('Error checking for existing cart:', findError);
    }
    
    // Step 2: If exists, update it - BUT PRESERVE critical fields if not explicitly provided
    if (existingCart) {
      console.log('📦 Found existing cart for session:', data.session_id, '- updating');
      
      // CRITICAL: Preserve defera_booking_reference from existing cart if new data doesn't include it
      // This prevents the booking reference from being lost when trackAbandonedTrip is called
      // multiple times (e.g., user changes seats, recalculates pricing, etc.)
      const existingFlightData = existingCart.flight_data || {};
      const newFlightData = tripData.flight_data || {};
      
      // Preserve these critical JSONB fields from existing cart if not in new data
      const preservedFlightDataFields = {
        defera_booking_reference: newFlightData.defera_booking_reference || existingFlightData.defera_booking_reference,
        _payment_method: newFlightData._payment_method ?? existingFlightData._payment_method,
        _miles_to_deplete: newFlightData._miles_to_deplete ?? existingFlightData._miles_to_deplete,
        _chose_refundable_installments: newFlightData._chose_refundable_installments ?? existingFlightData._chose_refundable_installments,
        // CRITICAL: Preserve travel credits if they exist in the cart
        credits: newFlightData.credits ?? existingFlightData.credits,
        credits_total: newFlightData.credits_total ?? existingFlightData.credits_total
      };
      
      // Merge: new flight data + preserved critical fields
      const mergedFlightData = {
        ...newFlightData,
        ...preservedFlightDataFields
      };
      
      console.log('📦 Preserving booking reference:', preservedFlightDataFields.defera_booking_reference, 
        '(from', newFlightData.defera_booking_reference ? 'new data' : 'existing cart', ')');
      
      // Log credits preservation for debugging
      if (preservedFlightDataFields.credits?.length > 0) {
        console.log('📦 Preserving travel credits:', preservedFlightDataFields.credits.length, 
          'credits, total:', preservedFlightDataFields.credits_total);
      }
      
      // Build update payload with merged flight_data
      const updatePayload = {
        ...tripData,
        flight_data: mergedFlightData,
        updated_at: new Date().toISOString()
      };
      
      // If passengers wasn't provided in the call, don't overwrite existing passengers
      // Check for explicit undefined vs null - undefined means "not provided", null means "explicitly clear"
      if (data.passengers === undefined) {
        delete updatePayload.passengers;
        console.log('📦 Preserving existing passengers (not overwriting)');
      }
      
      const { data: updatedCart, error: updateError } = await store.updateById(existingCart.id, updatePayload);

      if (updateError) {
        console.error('Error updating cart:', updateError);
        // Return existing cart even if update failed
        return new AbandonedTrip(existingCart);
      }
      
      console.log('📦 Cart updated for session:', data.session_id, 'ID:', updatedCart.id);
      return new AbandonedTrip(updatedCart);
    }
    
    // Step 3: No existing cart - insert new one
    console.log('📦 Creating new cart for session:', data.session_id);
    const { data: newCart, error: insertError } = await store.insert(tripData);

    if (insertError) {
      // Handle unique constraint violation (race condition - another request inserted first)
      if (insertError.code === '23505') {
        console.log('⚠️ Race condition - cart was created by another request, fetching it');
        const { data: raceCart } = await store.latestBySession(data.session_id);
        if (raceCart) {
          return new AbandonedTrip(raceCart);
        }
      }
      throw new Error(insertError.message);
    }

    console.log('📦 Cart created for session:', data.session_id, 'ID:', newCart.id);
    return new AbandonedTrip(newCart);
  }

  static async get(id) {
    const { supabase } = await import('../lib/supabase');
    const { User } = await import('./entities');

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Authentication required');
    }

    // Check if user is admin
    const currentUser = await User.getCurrentUser();
    const isAdmin = currentUser?.role === 'admin';

    if (isAdmin) {
      // Admin: Use backend endpoint to fetch cart with hotel data (bypasses RLS)
      try {
        const response = await fetch(`/api/admin/cart/${id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const { cart } = await response.json();
          if (cart) {
            return new AbandonedTrip(cart);
          }
        }
      } catch (err) {
        console.warn('Admin cart fetch failed, falling back to direct query:', err.message);
      }
    }

    // Regular users or fallback: Query directly from Supabase (RLS ensures they can only see their own)
    const { data: cart, error } = await supabase
      .from('abandoned_trips')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Failed to fetch cart:', error.message);
      return null;
    }

    return cart ? new AbandonedTrip(cart) : null;
  }

  static async update(id, data) {
    const { getCartStore } = await import('./cartStore');
    const store = await getCartStore();

    const updateData = {};
    if (data.status) updateData.status = data.status;
    if (data.step_reached) updateData.step_reached = data.step_reached;
    if (data.last_activity) updateData.last_activity = data.last_activity;
    if (data.seat_selections) updateData.seat_selections = data.seat_selections;
    if (data.passengers !== undefined) updateData.passengers = data.passengers;
    if (data.conversion_flight_id !== undefined) updateData.conversion_flight_id = data.conversion_flight_id;
    if (data.payment_link !== undefined) updateData.payment_link = data.payment_link;
    if (data.slicepay !== undefined) updateData.slicepay = data.slicepay;
    if (data.booking_reference !== undefined) updateData.booking_reference = data.booking_reference;
    
    // CRITICAL: Handle flight_data with proper JSONB merge to preserve critical fields
    // This prevents race conditions where concurrent updates (e.g., seat change + credits add) 
    // could overwrite each other's data
    if (data.flight_data !== undefined) {
      // Fetch existing cart to merge flight_data properly
      const { data: existingCart, error: fetchError } = await store.getById(id, 'flight_data');

      if (fetchError) {
        console.warn('Could not fetch existing cart for merge:', fetchError);
        updateData.flight_data = data.flight_data;
      } else {
        const existingFlightData = existingCart?.flight_data || {};
        const newFlightData = data.flight_data || {};
        
        // Merge: new flight_data overwrites, but preserve critical fields from existing if not in new
        updateData.flight_data = {
          ...existingFlightData,
          ...newFlightData,
          // Preserve these if not explicitly set in new data
          defera_booking_reference: newFlightData.defera_booking_reference || existingFlightData.defera_booking_reference,
          _payment_method: newFlightData._payment_method ?? existingFlightData._payment_method,
          _miles_to_deplete: newFlightData._miles_to_deplete ?? existingFlightData._miles_to_deplete,
          _chose_refundable_installments: newFlightData._chose_refundable_installments ?? existingFlightData._chose_refundable_installments,
          credits: newFlightData.credits ?? existingFlightData.credits,
          credits_total: newFlightData.credits_total ?? existingFlightData.credits_total
        };
        
        console.log('📦 AbandonedTrip.update: Merged flight_data with preserved fields', {
          hasCredits: !!(updateData.flight_data.credits?.length),
          creditsTotal: updateData.flight_data.credits_total
        });
      }
    }

    const { data: updatedTrip, error } = await store.updateById(id, updateData);

    if (error) {
      throw new Error(error.message);
    }

    return updatedTrip ? new AbandonedTrip(updatedTrip) : null;
  }

  static async delete(id) {
    if (!id) {
      throw new Error('Cannot delete cart without ID');
    }

    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Authentication required to delete cart');
    }

    const base = publicOrigin();
    const response = await fetch(`${base}/api/admin/abandoned-trips/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Failed to delete cart: ${response.status}`);
    }

    return true;
  }

  static async filter(filters = {}, orderBy = '-created_at', limit = null) {
    const { supabase } = await import('../lib/supabase');
    
    let query = supabase
      .from('abandoned_trips')
      .select('*');

    // Apply filters
    if (filters.user_email) {
      query = query.eq('user_email', filters.user_email);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.user_id) {
      query = query.eq('user_id', filters.user_id);
    }

    // Apply ordering
    const isDescending = orderBy.startsWith('-');
    const column = isDescending ? orderBy.substring(1) : orderBy;
    query = query.order(column, { ascending: !isDescending });

    // Apply limit
    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Failed to filter abandoned trips:', error);
      return [];
    }

    return (data || []).map(trip => new AbandonedTrip(trip));
  }

  static async list(orderBy = '-created_at', userId = null, options = {}) {
    const { supabase } = await import('../lib/supabase');
    
    // Check if user is admin or investor - if so, use backend endpoint to bypass RLS
    const currentUser = await User.me();
    const roles = currentUser?.app_metadata?.roles || [];
    const isAdmin = roles.includes('admin') || currentUser?.role === 'admin';
    const isInvestor = roles.includes('investor') || currentUser?.role === 'investor';
    
    if ((isAdmin || isInvestor) && !userId) {
      // Use backend endpoint for admin/investor to get ALL abandoned trips
      try {
        const session = await supabase.auth.getSession();
        const token = session?.data?.session?.access_token;
        
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch('/api/admin/abandoned-trips', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            page: options.page || 1,
            limit: options.limit || 40,
            searchTerm: options.searchTerm || ''
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch abandoned trips: ${response.statusText}`);
        }

        const result = await response.json();
        return (result.trips || []).map(trip => new AbandonedTrip(trip));
      } catch (error) {
        console.error('Error fetching abandoned trips via backend:', error);
        throw error;
      }
    }
    
    // Regular user flow OR admin viewing specific user - use Supabase directly (with RLS)
    const isDescending = orderBy.startsWith('-');
    const column = isDescending ? orderBy.substring(1) : orderBy;

    let query = supabase
      .from('abandoned_trips')
      .select('*');

    // Filter by specific user_id if provided (for admin viewing other users)
    if (userId) {
      query = query.eq('user_id', userId);
    }

    query = query.order(column, { ascending: !isDescending });

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    return (data || []).map(trip => new AbandonedTrip(trip));
  }

  async save() {
    const { supabase } = await import('../lib/supabase');

    if (this.id) {
      const { data, error } = await supabase
        .from('abandoned_trips')
        .update({
          session_id: this.session_id,
          user_email: this.user_email,
          flight_data: this.flight_data,
          seat_selections: this.seat_selections,
          total_amount: this.total_amount,
          departure_city: this.departure_city,
          arrival_city: this.arrival_city,
          departure_date: this.departure_date,
          trip_type: this.trip_type,
          marketing_airline: this.marketing_airline,
          status: this.status,
          step_reached: this.step_reached,
          last_activity: this.last_activity
        })
        .eq('id', this.id)
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return new AbandonedTrip(data);
    } else {
      const { authHelpers } = await import('../lib/supabase');
      const { user } = await authHelpers.getCurrentUser();

      const { data, error } = await supabase
        .from('abandoned_trips')
        .insert({
          user_id: user?.id || null,
          session_id: this.session_id,
          user_email: this.user_email,
          flight_data: this.flight_data,
          seat_selections: this.seat_selections,
          total_amount: this.total_amount,
          departure_city: this.departure_city,
          arrival_city: this.arrival_city,
          departure_date: this.departure_date,
          trip_type: this.trip_type,
          marketing_airline: this.marketing_airline,
          status: this.status,
          step_reached: this.step_reached,
          last_activity: this.last_activity
        })
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return new AbandonedTrip(data);
    }
  }
}

export class InternalComment {
  constructor(data = {}) {
    this.id = data.id || null;
    this.bookingId = data.bookingId || data.booking_id || null;
    this.userId = data.userId || data.user_id || null;
    this.comment = data.comment || '';
    this.isInternal = data.isInternal || data.is_internal || true;
    this.createdAt = data.createdAt || data.created_at || new Date().toISOString();
  }

  static async create(data) {
    const { supabase } = await import('../lib/supabase');
    
    // Get current user
    const currentUser = await User.me();
    if (!currentUser) {
      throw new Error('User must be logged in to create comments');
    }
    
    const { data: result, error } = await supabase
      .from('internal_comments')
      .insert({
        user_id: currentUser.id,
        booking_id: data.bookingId || data.booking_id,
        comment: data.comment,
        is_internal: data.isInternal !== undefined ? data.isInternal : true
      })
      .select()
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return new InternalComment(result);
  }

  async save() {
    if (!this.id) {
      return InternalComment.create(this);
    }

    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('internal_comments')
      .update({
        comment: this.comment,
        is_internal: this.isInternal
      })
      .eq('id', this.id)
      .select()
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return new InternalComment(data);
  }

  /**
   * The trip's internal comments through the server, for staff viewers. The
   * table's RLS admits admins only, so the concierge (read-only) can't use
   * findByBookingId below.
   */
  static async listForTrip(bookingId) {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    const res = await fetch(`/api/admin/internal-comments?booking_id=${encodeURIComponent(bookingId)}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
      credentials: 'omit',
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || `Failed to load comments (${res.status})`);
    return (body.comments || []).map((comment) => new InternalComment(comment));
  }

  static async findByBookingId(bookingId) {
    const { supabase } = await import('../lib/supabase');
    
    const { data, error } = await supabase
      .from('internal_comments')
      .select('*')
      .eq('booking_id', bookingId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(error.message);
    }

    return (data || []).map(comment => new InternalComment(comment));
  }

  static async delete(id) {
    const { supabase } = await import('../lib/supabase');

    const { error } = await supabase
      .from('internal_comments')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(error.message);
    }
  }
}

/**
 * Linked trips (server/trip-links.js): the staff banner at the top of a trip
 * listing the trips it travels with. Reading is open to any staff viewer;
 * link, unlink and the note are admin actions. Every call resolves to the
 * banner as the viewing trip now shows it.
 */
export class TripLinks {
  static async request(path, { method = 'GET', body } = {}) {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    const res = await fetch(path, {
      method,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'omit',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.error || `Linked trips request failed (${res.status})`);
      err.status = res.status;
      err.code = data.code || null;
      throw err;
    }
    return data;
  }

  static forTrip(bookingId) {
    return TripLinks.request(`/api/admin/trip-links?booking_id=${encodeURIComponent(bookingId)}`);
  }

  /** The customer's own "Traveling together" card: their linked trips, and
   *  only a count of linked trips on other accounts. Owner only. */
  static forMyTrip(bookingId) {
    return TripLinks.request(`/api/user/trip-links?booking_id=${encodeURIComponent(bookingId)}`);
  }

  /** Rejects with `code: 'different_account'` for someone else's trip until
   *  `confirmDifferentAccount` says staff checked with both travelers. */
  static link(bookingId, reference, note, { confirmDifferentAccount = false } = {}) {
    return TripLinks.request('/api/admin/trip-links', {
      method: 'POST',
      body: { booking_id: bookingId, reference, note, ...(confirmDifferentAccount ? { confirm_different_account: true } : {}) },
    });
  }

  /** Take `bookingId` out of its group; the reply is the banner for `viewBookingId`. */
  static unlink(bookingId, viewBookingId) {
    return TripLinks.request('/api/admin/trip-links/unlink', { method: 'POST', body: { booking_id: bookingId, view_booking_id: viewBookingId } });
  }

  static saveNote(bookingId, note) {
    return TripLinks.request('/api/admin/trip-links/note', { method: 'POST', body: { booking_id: bookingId, note } });
  }
}

export class UploadedFile {
  constructor(data = {}) {
    this.id = data.id || null;
    this.userId = data.userId || data.user_id || null;
    this.fileName = data.fileName || data.file_name || '';
    this.fileSize = data.fileSize || data.file_size || 0;
    this.fileType = data.fileType || data.file_type || '';
    this.fileUrl = data.fileUrl || data.file_url || '';
    this.fileBase64 = data.fileBase64 || data.file_base64 || '';
    this.createdAt = data.createdAt || data.created_at || new Date().toISOString();
  }

  static async create(data) {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to upload files');
    }

    const { supabase } = await import('../lib/supabase');
    
    // Check if Supabase has an authenticated session
    const { data: { session } } = await supabase.auth.getSession();
    console.log('🔵 Supabase session check:', session ? 'Session exists' : 'NO SESSION');
    
    if (!session) {
      throw new Error('No session found. Please log in again.');
    }
    
    const fileData = {
      user_id: currentUser.id,
      file_name: data.fileName || data.file_name || 'uploaded-file',
      file_size: data.fileSize || data.file_size || 0,
      file_type: data.fileType || data.file_type || 'application/octet-stream',
      file_url: data.fileUrl || data.file_url || '',
      file_base64: data.fileBase64 || data.file_base64 || ''
    };

    const { data: file, error } = await supabase
      .from('uploaded_files')
      .insert(fileData)
      .select()
      .single();

    if (error) {
      console.error('🔴 Insert error:', error);
      throw new Error(error.message);
    }

    return new UploadedFile(file);
  }

  static async findByUserId(userId) {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('uploaded_files')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(error.message);
    }

    return data.map(file => new UploadedFile(file));
  }

  static async findById(id) {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('uploaded_files')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      return null;
    }

    return data ? new UploadedFile(data) : null;
  }
}

// Auth implementation
export class User {
  // In-memory singleton for consistent same-tick access
  static _currentUser = null;

  constructor(data = {}) {
    this.id = data.id || null;
    this.email = data.email || '';
    this.firstName = data.firstName || data.first_name || '';
    this.lastName = data.lastName || data.last_name || '';
    this.role = data.role || 'user';
    this.app_metadata = data.app_metadata || {}; // Expose app_metadata for role checks
    this.user_metadata = data.user_metadata || {}; // Store user_metadata for referral source etc
    this.referral_source = data.referral_source || data.user_metadata?.referral_source || null;
    this.isAuthenticated = data.isAuthenticated || false;
    this.createdAt = data.createdAt || data.created_at || new Date().toISOString();
  }

  // Setter for in-memory singleton
  static setCurrentUser(user) {
    User._currentUser = user;
    console.log('✅ User singleton updated:', user?.email);
  }

  // Getter for in-memory singleton
  static getCachedCurrentUser() {
    return User._currentUser;
  }

  static async getCurrentUser() {
    // INDUSTRY STANDARD: Use Supabase's built-in session management
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session || !session.user) {
      User._currentUser = null; // Clear singleton when no session
      return null;
    }
    
    // Check in-memory singleton first (fastest)
    if (User._currentUser && User._currentUser.id === session.user.id) {
      return User._currentUser;
    }
    
    // Check localStorage cache second
    const storedUser = localStorage.getItem('defera.user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        // Version 4+ required for investor role support
        // Clear cache if version is old to force refresh from Supabase
        if (userData.version >= 4 && userData.email && userData.id && userData.id === session.user.id) {
          const user = new User({
            id: userData.id,
            email: userData.email,
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            role: userData.role || 'user',
            app_metadata: session.user.app_metadata || {}, // Get latest app_metadata from session
            profile_photo: userData.profile_photo || null,
            isAuthenticated: true,
            createdAt: userData.createdAt
          });
          User.setCurrentUser(user); // Update singleton
          return user;
        } else {
          // Clear outdated cache (force refresh from Supabase)
          localStorage.removeItem('defera.user');
        }
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
      }
    }
    
    // Use session metadata as source of truth
    try {
      const { authHelpers } = await import('../lib/supabase');
      const { user, error } = await authHelpers.getCurrentUser();
      
      if (error || !user) {
        return null;
      }
      
      // SECURITY FIX: Check app_metadata.roles (secure) instead of user_metadata.role (insecure)
      // After security migration, admin/investor roles are stored in app_metadata.roles array
      const roles = user.app_metadata?.roles || [];
      const isAdmin = roles.includes('admin');
      const isInvestor = roles.includes('investor');
      
      // Determine primary role: admin > investor > user
      let userRole = 'user';
      if (isAdmin) {
        userRole = 'admin';
      } else if (isInvestor) {
        userRole = 'investor';
      }
      
      console.log('🔍 DEBUG getCurrentUser - Email:', user.email);
      console.log('🔍 DEBUG getCurrentUser - app_metadata:', user.app_metadata);
      console.log('🔍 DEBUG getCurrentUser - roles array:', roles);
      console.log('🔍 DEBUG getCurrentUser - isAdmin:', isAdmin, 'isInvestor:', isInvestor, 'finalRole:', userRole);
      
      const newUser = new User({
        id: user.id,
        email: user.email,
        firstName: user.user_metadata?.first_name || user.user_metadata?.firstName || '',
        lastName: user.user_metadata?.last_name || user.user_metadata?.lastName || '',
        role: userRole,
        app_metadata: user.app_metadata, // Expose app_metadata for role checks
        profile_photo: user.user_metadata?.profile_photo || null,
        isAuthenticated: true,
        createdAt: user.created_at
      });
      User.setCurrentUser(newUser); // Update singleton
      return newUser;
    } catch (error) {
      // Supabase fallback failed, return null (localStorage already checked above)
      console.error('Supabase getCurrentUser fallback failed:', error);
      User._currentUser = null; // Clear singleton on failure
      return null;
    }
  }

  static async me() {
    return this.getCurrentUser();
  }

  static async list() {
    // Get Supabase session
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }
    if (!session.access_token) {
      throw new Error('Invalid session. Please log in again.');
    }

    // Use Vite proxy - cookies are stripped in proxy config to prevent 431 errors
    const response = await fetch(`/api/admin/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      credentials: 'omit'  // Prevent cookie bloat
    });

    // Check for auth errors (session expired)
    handleAuthError(response);

    if (!response.ok) {
      const responseText = await response.text();
      console.error('User API failed:', response.status, responseText.substring(0, 200));
      try {
        const error = JSON.parse(responseText);
        throw new Error(error.error || 'Failed to fetch users');
      } catch (e) {
        throw new Error(`Failed to fetch users: ${response.status} - ${responseText.substring(0, 100)}`);
      }
    }

    const data = await response.json();
    return data.users.map(u => new User({
      id: u.id,
      email: u.email,
      firstName: u.firstName,
      lastName: u.lastName,
      role: u.role,
      app_metadata: u.app_metadata || {}, // Include app_metadata for role checks
      custom_role: u.custom_role,
      profile_photo: u.profile_photo,
      createdAt: u.created_at
    }));
  }

  /**
   * The account that owns a trip, for the trip view's "Booked By" card. One
   * user, through an endpoint staff viewers (admin, concierge) can read —
   * findById below downloads every user and is admin/investor only.
   */
  static async findTripOwner(bookingId) {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    const response = await fetch(`/api/admin/trip-owner?booking_id=${encodeURIComponent(bookingId)}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
      credentials: 'omit',
    });
    handleAuthError(response);
    const body = await response.json().catch(() => ({}));
    if (!response.ok || !body.user) throw new Error(body.error || `Failed to load the account (${response.status})`);
    const user = body.user;
    return new User({
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      custom_role: user.custom_role,
      referral_source: user.referral_source,
      createdAt: user.created_at
    });
  }

  static async findById(userId) {
    // Get Supabase session
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }
    if (!session.access_token) {
      throw new Error('Invalid session. Please log in again.');
    }

    // Use Vite proxy - cookies are stripped in proxy config to prevent 431 errors
    const response = await fetch(`/api/admin/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      credentials: 'omit'  // Prevent cookie bloat
    });

    // Check for auth errors (session expired)
    handleAuthError(response);

    if (!response.ok) {
      const responseText = await response.text();
      console.error('User API failed:', response.status, responseText.substring(0, 200));
      try {
        const error = JSON.parse(responseText);
        throw new Error(error.error || 'Failed to fetch users');
      } catch (e) {
        throw new Error(`Failed to fetch users: ${response.status} - ${responseText.substring(0, 100)}`);
      }
    }

    const data = await response.json();
    const user = data.users.find(u => u.id === userId);
    
    if (!user) {
      throw new Error('User not found');
    }
    
    return new User({
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      custom_role: user.custom_role,
      referral_source: user.referral_source,
      createdAt: user.created_at
    });
  }

  static async filter(filters = {}) {
    // Get Supabase session - industry standard approach
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Not authenticated');
    }

    if (!session.access_token) {
      throw new Error('Invalid session. Please log in again.');
    }

    // Use Vite proxy - cookies are stripped in proxy config to prevent 431 errors
    const response = await fetch(`/api/admin/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      credentials: 'omit'  // Prevent cookie bloat
    });

    // Check for auth errors (session expired)
    handleAuthError(response);

    if (!response.ok) {
      const responseText = await response.text();
      console.error('User API failed:', response.status, responseText.substring(0, 200));
      try {
        const error = JSON.parse(responseText);
        throw new Error(error.error || 'Failed to fetch users');
      } catch (e) {
        throw new Error(`Failed to fetch users: ${response.status} - ${responseText.substring(0, 100)}`);
      }
    }

    const data = await response.json();
    let users = data.users.map(u => new User({
      id: u.id,
      email: u.email,
      firstName: u.firstName,
      lastName: u.lastName,
      role: u.role,
      custom_role: u.custom_role,
      createdAt: u.created_at
    }));

    // Apply filters
    if (filters.email) {
      users = users.filter(u => u.email === filters.email);
    }
    if (filters.role) {
      users = users.filter(u => u.role === filters.role);
    }

    return users;
  }

  static async updateMyUserData(data) {
    const currentUser = await this.getCurrentUser();
    if (!currentUser) {
      throw new Error('No user logged in');
    }
    
    // Get Supabase session
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No session found. Please log in again.');
    }
    if (!session.access_token) {
      throw new Error('Invalid session. Please log in again.');
    }
    
    const allowedMetadataKeys = ['firstName', 'lastName', 'first_name', 'last_name', 'role', 'profile_photo'];
    const metadata = {};
    
    if (data.firstName) metadata.first_name = data.firstName;
    if (data.lastName) metadata.last_name = data.lastName;
    if (data.first_name) metadata.first_name = data.first_name;
    if (data.last_name) metadata.last_name = data.last_name;
    if (data.role) metadata.role = data.role;
    if (data.profile_photo !== undefined) metadata.profile_photo = data.profile_photo;
    
    // Use Vite proxy - cookies are stripped in proxy config to prevent 431 errors
    const response = await fetch(`/api/users/me`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ metadata }),
      credentials: 'omit'  // Prevent cookie bloat
    });
    
    // Check for auth errors (session expired)
    handleAuthError(response);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update profile');
    }
    
    Object.assign(currentUser, data);
    return currentUser;
  }

  static async login(email, password) {
    console.log('🔐 UserEntity.login - using Supabase directly (industry standard)');
    
    // Clear cached user data only (Supabase manages its own session storage)
    console.log('Clearing cached user data...');
    localStorage.removeItem('defera.user');
    
    try {
      // INDUSTRY STANDARD: Authenticate directly with Supabase
      const { supabase } = await import('../lib/supabase');
      
      console.log('🔵 Signing in with Supabase...');
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('🔴 Supabase sign in error:', error.message);
        throw new Error(error.message);
      }

      if (!data.session || !data.user) {
        throw new Error('No session returned from Supabase');
      }

      console.log('✅ Supabase sign in successful! Session automatically persisted.');

      // INDUSTRY STANDARD: Use auth metadata as source of truth (no users table needed)
      // SECURITY FIX: Check app_metadata.roles (secure) instead of user_metadata.role (insecure)
      const roles = data.user.app_metadata?.roles || [];
      const isAdmin = roles.includes('admin');
      const isInvestor = roles.includes('investor');
      
      // Determine primary role: admin > investor > user
      let userRole = 'user';
      if (isAdmin) {
        userRole = 'admin';
      } else if (isInvestor) {
        userRole = 'investor';
      }
      
      const user = new User({
        id: data.user.id,
        email: data.user.email,
        firstName: data.user.user_metadata?.first_name || '',
        lastName: data.user.user_metadata?.last_name || '',
        role: userRole,
        app_metadata: data.user.app_metadata, // Expose app_metadata for role checks
        profilePhoto: data.user.user_metadata?.profile_photo || null,
        isAuthenticated: true,
        createdAt: data.user.created_at
      });

      // Cache user data in localStorage for quick access (Supabase manages session separately)
      const userDataToStore = {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        profile_photo: user.profilePhoto,
        createdAt: user.createdAt,
        version: 4  // Version 4: Supports investor role in addition to admin
      };
      
      localStorage.setItem('defera.user', JSON.stringify(userDataToStore));
      console.log('✅ User data cached in localStorage');
      
      console.log('✅ Login complete for user:', email);
      return user;
    } catch (error) {
      console.error('🔴 Login error:', error);
      throw error;
    }
  }

  static async signup(email, password, metadata = {}) {
    // CRITICAL: Clear all storage before signup to force fresh tokens
    // Prevents 431 errors from stale JWT tokens with bloated user_metadata
    console.log('Clearing all storage for fresh token...');
    localStorage.clear();
    sessionStorage.clear();
    
    try {
      // CRITICAL: Use Vite proxy path (relative /api) so cookie stripping works
      // Direct backend URLs bypass Vite proxy and cause 431 errors from cookie bloat
      const authUrl = `/api/auth/signup`;
      
      // Use Vite proxy for authentication - ensures cookies are stripped
      // NOTE: sign-up no longer collects a date of birth; a `dateOfBirth` from
      // an older caller is still forwarded as `date_of_birth`, which the
      // server checks and stores.
      const response = await fetch(authUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email, 
          password,
          metadata: {
            first_name: metadata.firstName,
            last_name: metadata.lastName,
            date_of_birth: metadata.dateOfBirth || metadata.date_of_birth || null,
            ...metadata
          }
        }),
        credentials: 'omit'  // Prevent cookie bloat - the main cause of 431 errors
      });

      const result = await response.json();

      if (!response.ok || result.error) {
        throw new Error(result.error || 'Signup failed');
      }

      const { data } = result;
      
      if (!data.user) {
        throw new Error('Signup failed');
      }

      if (data.user.identities && data.user.identities.length === 0) {
        throw new Error('An account with this email already exists. Please sign in instead.');
      }

      // AUTO-LOGIN: Establish authenticated session using returned tokens
      // The backend now returns session tokens after signup, so we can log the user in automatically
      if (data.session) {
        const { supabase } = await import('../lib/supabase');
        
        console.log('🔐 Setting session from signup response...');
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token
        });

        if (sessionError) {
          console.error('❌ Failed to establish session:', sessionError);
          throw new Error('Signup successful but failed to log in automatically. Please sign in manually.');
        }

        console.log('✅ Session established - user is now authenticated');
        
        // Get fresh user data with proper role from database
        const { data: { user: freshUser }, error: userError } = await supabase.auth.getUser();
        
        if (userError || !freshUser) {
          console.error('❌ Failed to get user data after signup:', userError);
          throw new Error('Signup successful but failed to retrieve user data. Please sign in manually.');
        }

        // Determine role from app_metadata (secure) - matches login flow
        const roles = freshUser.app_metadata?.roles || [];
        const isAdmin = roles.includes('admin');
        const isInvestor = roles.includes('investor');
        let userRole = 'user';
        if (isAdmin) {
          userRole = 'admin';
        } else if (isInvestor) {
          userRole = 'investor';
        }

        // Cache user data in localStorage for quick access (Supabase manages session separately)
        const userDataToStore = {
          id: freshUser.id,
          email: freshUser.email,
          firstName: metadata.firstName || freshUser.user_metadata?.first_name || '',
          lastName: metadata.lastName || freshUser.user_metadata?.last_name || '',
          role: userRole,
          profile_photo: freshUser.user_metadata?.profile_photo || null,
          createdAt: freshUser.created_at,
          version: 4  // Version 4: Supports investor role in addition to admin
        };
        
        localStorage.setItem('defera.user', JSON.stringify(userDataToStore));
        console.log('✅ User data cached in localStorage');

        return new User({
          id: freshUser.id,
          email: freshUser.email,
          firstName: userDataToStore.firstName,
          lastName: userDataToStore.lastName,
          role: userRole,
          isAuthenticated: true,
          createdAt: freshUser.created_at
        });
      }

      // Fallback: No session returned (shouldn't happen with auto-login enabled)
      console.warn('⚠️ No session returned from signup - user will need to login manually');
      return new User({
        id: data.user.id,
        email: data.user.email,
        firstName: metadata.firstName || '',
        lastName: metadata.lastName || '',
        role: 'user',
        isAuthenticated: false,
        createdAt: data.user.created_at
      });
    } catch (error) {
      console.error('Signup exception:', error);
      throw error;
    }
  }

  static async logout() {
    // iOS app: stop this phone's notifications for the account first, while
    // the session still works (nothing on the web).
    await import('@/native/push').then((m) => m.forgetThisPhone()).catch(() => {});
    const { authHelpers } = await import('../lib/supabase');
    const { error } = await authHelpers.signOut();
    
    if (error) {
      throw new Error(error.message);
    }
    
    return true;
  }

  static async resetPassword(email) {
    const { supabase } = await import('../lib/supabase');
    // Redirect to auth callback which will handle the recovery token and then redirect to reset-password page.
    // 1. Allowlist this exact URL: Supabase Dashboard → Auth → URL Configuration → Redirect URLs.
    // 2. For reliable reset links (especially cross-device), set the "Reset password" email template
    //    link to: {{ .RedirectTo }}?token_hash={{ .TokenHash }}&type=recovery — see docs/PASSWORD_RESET_SETUP.md.
    const redirectTo = `${publicOrigin()}/auth/callback`;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo
    });
    
    if (error) {
      console.error('Password reset failed - full Supabase error:', {
        message: error.message,
        code: error.code,
        status: error.status,
        name: error.name,
        fullError: error
      });
      // Keep Supabase's message for logs, but carry status + code so the UI can
      // classify the failure (rate limit vs bad address vs offline) instead of
      // showing developer wording to a customer — see src/lib/authErrors.js.
      const wrapped = new Error(error.message);
      wrapped.status = error.status;
      wrapped.code = error.code;
      throw wrapped;
    }
    
    return true;
  }

  static async updatePassword(newPassword) {
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    
    if (error) {
      throw new Error(error.message);
    }
    
    return true;
  }

  static async loginWithGoogle() {
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${publicOrigin()}/auth/callback`
      }
    });
    
    if (error) {
      throw new Error(error.message);
    }
    
    return true;
  }

  static async loginWithApple() {
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: `${publicOrigin()}/auth/callback`
      }
    });
    
    if (error) {
      throw new Error(error.message);
    }
    
    return true;
  }

  async save() {
    return User.updateMyUserData(this);
  }
}

// User Preferences entity
export class UserPreferences {
  constructor(data = {}) {
    this.id = data.id || null;
    this.userId = data.userId || data.user_id || null;
    this.searchEnginePreferences = data.searchEnginePreferences || data.search_engine_preferences || { duffel: true, googleFlights: true };
    
    const milesData = data.defera_miles || data.deferaMiles || {};
    this.deferaMiles = {
      balance: parseFloat(milesData.balance || 0),
      earnedTotal: parseFloat(milesData.earned_total || milesData.earnedTotal || 0),
      redeemedTotal: parseFloat(milesData.redeemed_total || milesData.redeemedTotal || 0),
      history: milesData.history || []
    };
    
    this.dismissedExtensionRequests = data.dismissed_extension_requests || data.dismissedExtensionRequests || [];
    
    this.createdAt = data.createdAt || data.created_at || new Date().toISOString();
    this.updatedAt = data.updatedAt || data.updated_at || new Date().toISOString();
  }

  // Get current user's preferences
  static async get() {
    const { User } = await import('./entities');
    const { supabase } = await import('../lib/supabase');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      // Return defaults if not logged in
      return new UserPreferences();
    }

    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', currentUser.id)
      .single();

    // Get miles from user metadata (bypasses schema cache)
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    const milesFromMetadata = user?.user_metadata?.defera_miles || null;

    if (error) {
      // If no preferences exist yet, return defaults with miles from metadata
      if (error.code === 'PGRST116') {
        return new UserPreferences({ 
          user_id: currentUser.id,
          defera_miles: milesFromMetadata
        });
      }
      throw new Error(error.message);
    }

    // Merge preferences data with miles from metadata (metadata takes precedence)
    return new UserPreferences({
      ...data,
      defera_miles: milesFromMetadata || data.defera_miles
    });
  }

  // Update preferences
  static async update(preferences) {
    const { User } = await import('./entities');
    const { supabase } = await import('../lib/supabase');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to update preferences');
    }

    // Try to update first
    const { data: updateData, error: updateError } = await supabase
      .from('user_preferences')
      .update({
        search_engine_preferences: preferences,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', currentUser.id)
      .select()
      .single();

    if (!updateError) {
      return new UserPreferences(updateData);
    }

    // If update failed because no record exists, insert a new one
    if (updateError.code === 'PGRST116') {
      const { data: insertData, error: insertError} = await supabase
        .from('user_preferences')
        .insert({
          user_id: currentUser.id,
          search_engine_preferences: preferences
        })
        .select()
        .single();

      if (insertError) {
        throw new Error(insertError.message);
      }

      return new UserPreferences(insertData);
    }

    throw new Error(updateError.message);
  }

  // Get current user's miles balance from ledger
  static async getMilesBalance() {
    const { User } = await import('./entities');
    const { supabase } = await import('../lib/supabase');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      return 0;
    }

    // Get auth token for API call
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      console.warn('No active session, returning 0 miles balance');
      return 0;
    }

    // Call ledger-based balance endpoint
    const response = await fetch(`${publicOrigin()}/api/miles/balance/${currentUser.id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    const text = await response.text();
    let result;
    try {
      result = text ? JSON.parse(text) : {};
    } catch {
      if (response.status === 429) throw new Error('Too many requests. Please try again in a few minutes.');
      throw new Error(text || 'Failed to fetch miles balance');
    }

    if (!response.ok) {
      console.error('Failed to get miles balance:', result?.error);
      throw new Error(result?.error || 'Failed to fetch miles balance');
    }

    return result.balance ?? 0;
  }

  // SECURE: Add miles to user's account (when earning from bookings)
  // Calls server-side API endpoint to prevent client-side forgery
  static async addMiles(amount, reason = 'Booking completed', bookingId = null) {
    const { User } = await import('./entities');
    const { supabase } = await import('../lib/supabase');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to earn miles');
    }

    // Get auth token for API call
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('Authentication required to earn miles');
    }

    // Call secure server-side endpoint
    const response = await fetch(`${publicOrigin()}/api/miles/earn`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        amount: parseFloat(amount),
        reason,
        bookingId
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to earn miles');
    }

    return result;
  }

  // SECURE: Deduct miles from user's account (when redeeming)
  // Calls server-side API endpoint to prevent client-side forgery
  static async deductMiles(amount, reason = 'Miles redeemed') {
    const { User } = await import('./entities');
    const { supabase } = await import('../lib/supabase');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to redeem miles');
    }

    // Get auth token for API call
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('Authentication required to redeem miles');
    }

    // Call secure server-side endpoint
    const response = await fetch(`${publicOrigin()}/api/miles/redeem`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        amount: parseFloat(amount),
        reason
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to redeem miles');
    }

    return result;
  }

  // SECURE: Admin manually redeem miles (for refunds, adjustments, etc)
  // Calls server-side API endpoint with admin role verification
  static async adminRedeemMiles(targetUserId, amount, reason) {
    const { User } = await import('./entities');
    const { supabase } = await import('../lib/supabase');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('Authentication required');
    }

    // Get auth token for API call
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('Authentication required to redeem miles');
    }

    // Call secure server-side endpoint (will verify admin role)
    const response = await fetch(`${publicOrigin()}/api/miles/admin-redemption`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        targetUserId,
        amount: parseFloat(amount),
        reason
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to redeem miles');
    }

    return result;
  }

  // Get miles ledger transaction history
  static async getMilesLedger(userId = null) {
    const { User } = await import('./entities');
    const { supabase } = await import('../lib/supabase');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in');
    }

    const targetUserId = userId || currentUser.id;

    // Get auth token for API call
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('Authentication required');
    }

    // Call ledger endpoint
    const response = await fetch(`${publicOrigin()}/api/miles/ledger/${targetUserId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to fetch ledger');
    }

    return result.transactions || [];
  }

  // Get dismissed extension request IDs
  static async getDismissedExtensionRequests() {
    const { User } = await import('./entities');
    const { supabase } = await import('../lib/supabase');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      return [];
    }

    const { data, error } = await supabase
      .from('user_preferences')
      .select('dismissed_extension_requests')
      .eq('user_id', currentUser.id)
      .single();

    if (error) {
      // If no preferences exist yet, return empty array
      if (error.code === 'PGRST116') {
        return [];
      }
      console.error('Error fetching dismissed extension requests:', error);
      return [];
    }

    return data?.dismissed_extension_requests || [];
  }

  // Add a dismissed extension request ID
  static async dismissExtensionRequest(requestId) {
    const { User } = await import('./entities');
    const { supabase } = await import('../lib/supabase');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to dismiss requests');
    }

    // Get current dismissed requests
    const currentDismissed = await this.getDismissedExtensionRequests();
    
    // Add the new ID if it's not already there
    if (currentDismissed.includes(requestId)) {
      return currentDismissed; // Already dismissed
    }

    const updatedDismissed = [...currentDismissed, requestId];

    // Try to update first
    const { error: updateError } = await supabase
      .from('user_preferences')
      .update({
        dismissed_extension_requests: updatedDismissed,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', currentUser.id);

    // If update failed because no record exists, insert a new one
    if (updateError && updateError.code === 'PGRST116') {
      const { error: insertError } = await supabase
        .from('user_preferences')
        .insert({
          user_id: currentUser.id,
          dismissed_extension_requests: updatedDismissed
        });

      if (insertError) {
        throw new Error(insertError.message);
      }
    } else if (updateError) {
      throw new Error(updateError.message);
    }

    return updatedDismissed;
  }
}

// Airport entity for managing airport data from Supabase
export class Airport {
  constructor(data = {}) {
    this.id = data.id || null;
    this.code = data.code || '';
    this.city = data.city || '';
    this.airport = data.airport || '';
    this.country = data.country || '';
    // Set when search offers this airport in place of one it doesn't
    // ("Near Teterboro"): the city typed.
    this.nearbyFor = data.nearbyFor || null;
    // Set when search found this airport by the city it serves, not its own
    // name ("Serves Medellin" on MDE, which is in Rionegro).
    this.servesName = data.servesName || null;
    this.createdAt = data.created_at || data.createdAt || new Date().toISOString();
  }

  // Get all airports
  static async list() {
    const { supabase } = await import('../lib/supabase');
    
    const { data, error } = await supabase
      .from('airports')
      .select('*')
      .order('city', { ascending: true });

    if (error) {
      console.error('Failed to load airports:', error.message);
      return [];
    }

    return data.map(airport => new Airport(airport));
  }

  // Search airports by term (code, city, airport name, or country). Runs in
  // the browser over the copied airport list, src/data/searchAirports.js;
  // see src/lib/airportSearch.js for what it offers and in what order.
  static async search(term) {
    if (!term || term.length < 2) return [];
    const { searchAirports } = await import('../lib/airportSearch');
    const results = await searchAirports(term);
    return results.map(airport => new Airport(airport));
  }

  // Get airport by code
  static async getByCode(code) {
    const { supabase } = await import('../lib/supabase');
    
    const { data, error} = await supabase
      .from('airports')
      .select('*')
      .eq('code', code.toUpperCase())
      .single();

    if (error || !data) {
      // Not in the table (a new code such as DJT): the copied search list.
      try {
        const { findSearchAirport } = await import('../lib/airportSearch');
        const listed = await findSearchAirport(code);
        if (listed) return new Airport(listed);
      } catch {
        // Fall through to "not found".
      }
      if (error) console.error('Failed to get airport:', error.message);
      return null;
    }

    return new Airport(data);
  }
}

// ============================================================================
// StayBooking Entity - Hotel Bookings for Flights + Stays Packages
// ============================================================================

export class StayBooking {
  constructor(data = {}) {
    this.id = data.id || null;
    this.userId = data.userId || data.user_id || null;
    this.bookingId = data.bookingId || data.booking_id || null;
    this.serpapiHotelId = data.serpapiHotelId || data.serpapi_hotel_id || null;
    this.name = data.name || '';
    
    // Location
    this.address = data.address || null;
    this.latitude = data.latitude || null;
    this.longitude = data.longitude || null;
    this.gpsCoordinates = data.gpsCoordinates || data.gps_coordinates || null;
    
    // Dates
    this.checkInDate = data.checkInDate || data.check_in_date || null;
    this.checkOutDate = data.checkOutDate || data.check_out_date || null;
    this.nights = data.nights || null;
    
    // Pricing
    this.nightlyRate = data.nightlyRate || data.nightly_rate || 0;
    this.totalAmount = data.totalAmount || data.total_amount || 0;
    this.currency = data.currency || 'USD';
    
    // Hotel details
    this.hotelClass = data.hotelClass || data.hotel_class || null;
    this.rating = data.rating || null;
    // What `rating` is out of (10 Duffel, 5 Google). Saved rows keep it in
    // metadata.rating_scale; see src/utils/hotelRating.js.
    this.ratingScale = data.ratingScale ?? data.rating_scale ?? data.metadata?.rating_scale ?? null;
    this.reviews = data.reviews || 0;
    
    // Room details from metadata
    const metadata = data.metadata || {};
    this.roomType = metadata.room_name || null;
    
    // Parse bed type - can be array of objects, array of strings, or string
    if (metadata.beds) {
      if (Array.isArray(metadata.beds)) {
        // If array of objects like [{type: "King", count: 1}], extract descriptions
        if (metadata.beds.length > 0 && typeof metadata.beds[0] === 'object') {
          this.bedType = metadata.beds
            .map(bed => `${bed.count || 1} ${bed.type || 'Bed'}`)
            .join(', ');
        } else {
          // If array of strings
          this.bedType = metadata.beds.join(', ');
        }
      } else {
        // If plain string
        this.bedType = metadata.beds;
      }
    } else {
      this.bedType = null;
    }
    
    // Additional data
    this.amenities = data.amenities || [];
    this.images = data.images || [];
    this.thumbnail = data.thumbnail || null;
    this.link = data.link || null;
    this.sponsored = data.sponsored || false;
    this.ecoCertified = data.ecoCertified || data.eco_certified || false;
    this.freeCancellation = data.freeCancellation || data.free_cancellation || false;
    this.nearbyPlaces = data.nearbyPlaces || data.nearby_places || [];
    this.metadata = data.metadata || {};
    
    // Timestamps
    this.createdAt = data.createdAt || data.created_at || new Date().toISOString();
    this.updatedAt = data.updatedAt || data.updated_at || new Date().toISOString();
  }

  // Get stays for a specific user
  static async findByUserId(userId) {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('stays')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(error.message);
    }

    return (data || []).map(stay => new StayBooking(stay));
  }

  // Get stay by ID
  static async findById(id) {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('stays')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      return null;
    }

    return data ? new StayBooking(data) : null;
  }

  // Get stay associated with a booking
  static async findByBookingId(bookingId) {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('stays')
      .select('*')
      .eq('booking_id', bookingId)
      .single();

    if (error) {
      return null;
    }

    return data ? new StayBooking(data) : null;
  }

  // Get most recent stay by session_id (for abandoned trips)
  // Uses authenticated backend endpoint (bypasses PostgREST cache issue)
  static async findBySessionId(sessionId) {
    const { supabase } = await import('../lib/supabase');
    
    try {
      // Get current session token for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.error('No authentication token available');
        return null;
      }

      const response = await fetch(`/api/duffel-stays/by-session/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (!response.ok) {
        console.error('Failed to fetch stay by session_id:', response.statusText);
        return null;
      }
      
      const { stay } = await response.json();
      return stay ? new StayBooking(stay) : null;
    } catch (error) {
      console.error('Error fetching stay by session_id:', error);
      return null;
    }
  }

  // Create a new stay
  static async create(stayData) {
    const { User } = await import('./entities');
    const currentUser = await User.getCurrentUser();
    
    if (!currentUser) {
      throw new Error('User must be logged in to create stays');
    }

    const { supabase } = await import('../lib/supabase');
    
    const { data, error } = await supabase
      .from('stays')
      .insert({
        user_id: currentUser.id,
        booking_id: stayData.bookingId || stayData.booking_id || null,
        serpapi_hotel_id: stayData.serpapiHotelId || stayData.serpapi_hotel_id || null,
        name: stayData.name,
        address: stayData.address,
        latitude: stayData.latitude,
        longitude: stayData.longitude,
        check_in_date: stayData.checkInDate || stayData.check_in_date,
        check_out_date: stayData.checkOutDate || stayData.check_out_date,
        nightly_rate: stayData.nightlyRate || stayData.nightly_rate,
        total_amount: stayData.totalAmount || stayData.total_amount,
        currency: stayData.currency || 'USD',
        hotel_class: stayData.hotelClass || stayData.hotel_class,
        rating: stayData.rating,
        reviews: stayData.reviews || 0,
        amenities: stayData.amenities || [],
        images: stayData.images || [],
        metadata: stayData.metadata || {}
      })
      .select()
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return new StayBooking(data);
  }

  // Update stay
  async update(updates) {
    if (!this.id) {
      throw new Error('Cannot update stay without ID');
    }

    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('stays')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', this.id)
      .select()
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return new StayBooking(data);
  }

  // Delete stay
  static async delete(id) {
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase
      .from('stays')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(error.message);
    }
  }

  // Link stay to a booking
  async linkToBooking(bookingId) {
    return this.update({ booking_id: bookingId });
  }

  // Get formatted check-in/check-out dates
  getFormattedDates() {
    const checkIn = new Date(this.checkInDate);
    const checkOut = new Date(this.checkOutDate);
    
    return {
      checkIn: checkIn.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      checkOut: checkOut.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    };
  }

  // Convert to plain object for API transmission
  toJSON() {
    return {
      id: this.id,
      user_id: this.userId,
      booking_id: this.bookingId,
      serpapi_hotel_id: this.serpapiHotelId,
      name: this.name,
      address: this.address,
      latitude: this.latitude,
      longitude: this.longitude,
      check_in_date: this.checkInDate,
      check_out_date: this.checkOutDate,
      nights: this.nights,
      nightly_rate: this.nightlyRate,
      total_amount: this.totalAmount,
      currency: this.currency,
      hotel_class: this.hotelClass,
      rating: this.rating,
      reviews: this.reviews,
      amenities: this.amenities,
      images: this.images,
      metadata: this.metadata,
      created_at: this.createdAt,
      updated_at: this.updatedAt
    };
  }
}